import { isSameDay } from "date-fns";
import { type TranslateResult } from "vue-i18n";

export const useCourseSessionInfo = (
  session: DatabaseTable<"course_sessions"> & {
    place?: DatabaseTable<"places"> | null;
  },
  options?: { locale?: string },
) => {
  const { $i18n } = useNuxtApp();

  const isOneDaySession = isSameDay(
    new Date(session.start_date as string),
    new Date(session.end_date as string),
  );

  const formattedDate = computed(() => {
    if (isOneDaySession) {
      return useI18nDate(session.start_date ?? "", {
        timeZone: session.timezone,
        month: "short",
        weekday: "short",
        day: "numeric",
        locale: options?.locale,
      }).value;
    }

    const start = useI18nDate(session.start_date ?? "", {
      timeZone: session.timezone,
      month: "short",
      day: "numeric",
      locale: options?.locale,
    }).value;
    const end = useI18nDate(session.end_date ?? "", {
      timeZone: session.timezone,
      month: "short",
      day: "numeric",
      locale: options?.locale,
    }).value;

    return `${start} - ${end}`;
  });

  const formattedPlaceLabel = computed(() => {
    if (!session.place) return;

    return `${session.place.name}, ${session.place.address.street_number} ${session.place.address.street} ${session.place.address.zip_code}`;
  });

  const formattedSchedule = computed(() => {
    return session.schedule.map(({ start_date, end_date }) => {
      const date = useI18nDate(start_date!, {
        dateStyle: "medium",
        timeZone: session.timezone,
        locale: options?.locale,
      });
      const start = useI18nDate(start_date ?? "", {
        timeStyle: "short",
        timeZone: session.timezone,
        locale: options?.locale,
      });
      const end = useI18nDate(end_date ?? "", {
        timeStyle: "short",
        timeZone: session.timezone,
        locale: options?.locale,
      });

      return `${date.value} - ${start.value} - ${end.value}`;
    });
  });

  const formattedTime = computed(() => {
    if (!isOneDaySession) return;

    const start = useI18nDate(session.start_date ?? "", {
      timeStyle: "short",
      timeZone: session.timezone,
      locale: options?.locale,
    }).value;
    const end = useI18nDate(session.end_date ?? "", {
      timeStyle: "short",
      timeZone: session.timezone,
      locale: options?.locale,
    }).value;

    return `${start} - ${end}`;
  });

  const experienceLabel = computed(() => {
    const list: TranslateResult[] = [];

    if (session.options.breakfast_included) {
      list.push($i18n.global.t("session.experience.breakfast"));
    }

    if (session.options.lunch_included) {
      list.push($i18n.global.t("session.experience.lunch"));
    }

    if (list.length === 0) return;

    const formattedList = useI18nList(list).value;

    return $i18n.global.t("session.experience.included_label", {
      list: formattedList.charAt(0).toUpperCase() + formattedList.slice(1),
    });
  });

  return {
    formattedSchedule,
    formattedDate,
    formattedTime,
    formattedPlaceLabel,
    experienceLabel,
  };
};
